import React from 'react';
import PropTypes from 'prop-types';
import {
  Body,
  Box,
  Flex,
  Heading,
  RawMoney,
} from '@edreamsodigeo/prisma-design-system';
import {
  FilledNumberChronology,
  EmptyNumberChronology,
  ChronologyContent,
} from '@/components/Chronology';
import {
  Trans,
  useTranslation,
  useFlow,
  createFeature,
} from '@edreamsodigeo/frontend-core';
import {
  PriceOfferAlert,
  PriceOfferHeaderSubtitle,
  StepSubtitle,
  StyledPrice,
} from '@/components/PriceOffer/PriceOffer.utils';
import { PrimeChronologyStep } from '../PrimeChronologyStep';
import { useLogin } from '@/providers/login/LoginProvider';
import { PRIME_FIRST_PHASE_1 } from '@/versionedComponents';
import { usePrimeBenefitsLink } from '@/providers/PrimeBenefitsLinkProvider';
import { FreePill } from '@/components/FreePill/FreePill';

const PillStyledPrice = ({ money }) => (
  <RawMoney rounded type="none" size="tiny" currency={money.currency}>
    {money.amount}
  </RawMoney>
);

export const PriceOffer = ({
  money = {},
  standardDeposit = {},
  days,
  noFeeKey,
}) => {
  return (
    <>
      <PriceOfferDescription />
      <PriceOfferStepsVariation
        money={money}
        standardDeposit={standardDeposit}
        days={days}
        noFeeKey={noFeeKey}
      />
    </>
  );
};

export const PriceOfferNoDescription = ({ money = {}, days, noFeeKey }) => {
  return (
    <PriceOfferStepsVariation money={money} days={days} noFeeKey={noFeeKey} />
  );
};

const PriceOfferDescription = () => {
  const { t } = useTranslation();

  return (
    <Box mb="base" mt="1x_small">
      <Heading type="3" weight="medium">
        {t('pricefreeze.valueprop.header')}
      </Heading>

      <PriceOfferHeaderSubtitle />
    </Box>
  );
};

const NonPrimeFirstStep = ({ money }) => {
  const { t } = useTranslation();
  const primeBenefitsLink = usePrimeBenefitsLink();

  return (
    <FilledNumberChronology number={1}>
      <ChronologyContent
        heading={t('pricefreeze.valueprop.step1.heading')}
        title={
          <Trans
            i18nKey="pricefreeze.valueprop.step1.primeFirst.title"
            components={{ price: <StyledPrice money={money} /> }}
          />
        }
        active
      >
        <StepSubtitle color="neutral.70">
          <Trans
            i18nKey="pricefreeze.valueprop.step1.primeFirst.subtitle"
            components={{
              subtitleLink: primeBenefitsLink,
            }}
          />
        </StepSubtitle>
      </ChronologyContent>
    </FilledNumberChronology>
  );
};

const PrimeFirstStep = ({ money = {}, noFeeKey }) => {
  const { t } = useTranslation();
  return (
    <FilledNumberChronology number={1}>
      <ChronologyContent
        heading={t('pricefreeze.valueprop.step1.heading')}
        title={
          money.amount !== 0 ? (
            <Trans
              i18nKey="pricefreeze.valueprop.step1.title"
              components={{ price: <StyledPrice money={money} /> }}
            />
          ) : (
            t('freeze.flight.price')
          )
        }
        active
        pill={
          money.amount === 0 ? (
            <Flex
              flexDirection="column"
              justifyContent="center"
              alignItems="flex-end"
            >
              <FreePill animated={false}>
                <Trans
                  i18nKey={'pricefreeze.free.pill.title'}
                  components={{
                    priceKey: <PillStyledPrice money={money} />,
                  }}
                />
              </FreePill>
              <Box ml="2x_small">
                <Body type="xSmall" color="neutral.70">
                  {t('freeze.with.prime')}
                </Body>
              </Box>
            </Flex>
          ) : (
            <></>
          )
        }
      >
        {money.amount !== 0 ? (
          <StepSubtitle color="neutral.70">
            {t('pricefreeze.valueprop.step1.total.subtitle')}
          </StepSubtitle>
        ) : null}
        {money.amount !== 0 ? <PriceOfferAlert noFeeKey={noFeeKey} /> : null}
      </ChronologyContent>
    </FilledNumberChronology>
  );
};

const PriceOfferSteps = ({ money = {}, days, noFeeKey }) => {
  const { t } = useTranslation();
  return (
    <Box pl="2x_small">
      <PrimeFirstStep money={money} noFeeKey={noFeeKey} />
      <EmptyNumberChronology number={2} last={true}>
        <ChronologyContent
          heading={t('pricefreeze.valueprop.step2.heading', {
            days: `${days}`,
          })}
          title={
            money.amount !== 0
              ? t('pricefreeze.valueprop.step2.title')
              : t('book.trip')
          }
        >
          {money.amount !== 0 ? (
            <StepSubtitle>
              <Box mb="base">
                {t('pricefreeze.valueprop.step2.total.subtitle')}
              </Box>
            </StepSubtitle>
          ) : null}
        </ChronologyContent>
      </EmptyNumberChronology>
    </Box>
  );
};

const PriceOfferStepsPrimeFirst = ({ money = {}, days, noFeeKey }) => {
  const { t } = useTranslation();
  const { isPrime } = useLogin();
  const isMobile = useFlow() === 'mobile';
  const showPrimeStep = isMobile && !isPrime;

  return (
    <Box pl="2x_small">
      {showPrimeStep ? (
        <NonPrimeFirstStep money={money} />
      ) : (
        <PrimeFirstStep money={money} noFeeKey={noFeeKey} />
      )}
      <EmptyNumberChronology number={2} last={!showPrimeStep}>
        <ChronologyContent
          heading={t('pricefreeze.valueprop.step2.heading', {
            days: `${days}`,
          })}
          title={t('pricefreeze.valueprop.step2.title')}
        >
          <StepSubtitle>
            <Box mt="2x_small">
              <Body color="neutral.90">
                {t('pricefreeze.valueprop.step2.total.subtitle')}
              </Body>
            </Box>
          </StepSubtitle>
        </ChronologyContent>
      </EmptyNumberChronology>
      {showPrimeStep && <PrimeChronologyStep stepIndex={3} lastStep />}
    </Box>
  );
};

export const PriceOfferStepsVariation = createFeature({
  name: PRIME_FIRST_PHASE_1,
  variations: [PriceOfferSteps, PriceOfferStepsPrimeFirst],
  version: 1,
});

PriceOffer.propTypes = {
  money: PropTypes.shape({
    amount: PropTypes.number.isRequired,
    currency: PropTypes.string.isRequired,
  }).isRequired,
  days: PropTypes.number.isRequired,
};
